
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * CookieBanner component – shows a consent banner after a short delay
 * if the user hasn't already stored a consent decision.
 *
 * The component now safely accesses `localStorage` (which can be unavailable
 * in some environments, e.g. server‑side rendering or privacy‑focused browsers)
 * and includes defensive error handling.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  // -------------------------------------------------------------------------
  // Helper: safe access to localStorage
  // -------------------------------------------------------------------------
  const getLocalStorageItem = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (err) {
      // If localStorage is disabled or unavailable we simply treat it as
      // “no consent stored” and let the banner appear.
      console.warn('localStorage unavailable:', err);
      return null;
    }
  };

  const setLocalStorageItem = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (err) {
      // Silently fail – the user will see the banner again next visit.
      console.warn('Failed to write to localStorage:', err);
    }
  };

  // -------------------------------------------------------------------------
  // Effect: decide whether to show the banner
  // -------------------------------------------------------------------------
  useEffect(() => {
    const consent = getLocalStorageItem('cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleAccept = () => {
    setLocalStorageItem('cookie_consent', 'accepted');
    setVisible(false);
  };

  const handleReject = () => {
    setLocalStorageItem('cookie_consent', 'rejected');
    setVisible(false);
  };

  // If the banner shouldn't be shown, render nothing.
  if (!visible) return null;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up">
      <div className="bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-cookie-line text-lg text-primary" />
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Utilizamos cookies para mejorar tu experiencia en nuestro sitio. Al continuar navegando, aceptas nuestra{' '}
              <Link
                to="/cookies"
                className="text-primary font-medium hover:underline cursor-pointer"
              >
                Política de Cookies
              </Link>{' '}
              y{' '}
              <Link
                to="/privacy"
                className="text-primary font-medium hover:underline cursor-pointer"
              >
                Política de Privacidad
              </Link>
              .
            </p>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={handleReject}
              className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Rechazar
            </button>

            <button
              onClick={handleAccept}
              className="px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              Aceptar Cookies
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
